// ComposedExperienceRisograph — Tier-A "risograph" skin for composed experiences.
//
// A per-world treatment of the composed runtime, sibling to ComposedExperience-
// Aurora / -Obsidian, proving the dispatcher pattern generalises across visual
// registers. Same data + capture contract as ComposedExperienceSlide (both
// consume useComposedExperience), rendered in the RISOGRAPH print language shared
// with RisographPerspectiveLenses: cream paper, spot-ink overprint, ~2px
// misregistration, chunky Anton headlines, hard offset shadows, rubber-stamp
// marks. The standout is a re-rate rate-slider that prints the learner's earlier
// reading in one ink and their revised one in a second, OFFSET and multiply-
// blended (a real riso misprint), then SLAMS a "MOVED −N" stamp between them so a
// change of mind is made visible (§8.12b: juice = cognition). Where Aurora's
// signature was the luminous ribbon, Risograph's is the two-ink overprint diff.

import { useEffect, useState } from 'react';
import type { Theme } from '../../slideTypes';
import type { ComposedExperienceSlideData } from '../../slideTypes';
import type { ActivityResponsePayload } from '../../../../../types';
import type {
  ChoiceBlock,
  CommitBoxBlock,
  CompositionBlock,
  ConfidenceBlock,
  FlagInTextBlock,
  PerspectiveLensBlock,
  RateSliderBlock,
  RevealBlock,
  SelectEvidenceBlock,
  SortZonesBlock,
  StimulusBlock,
} from '../../../../../lib/scenario-builder/composition/types';
import type { Answers, BlockAnswer } from '../../../../../lib/scenario-builder/composition/capture';
import { ExperienceClose } from '../../shared/ExperienceClose';
import { ThemeWorldAtmosphere } from '../../../../../lib/scenario-themes/Atmosphere';
import {
  useThemeWorldAudio,
  playRisoStamp,
  playRisoRoller,
} from '../../../../../lib/scenario-themes/audio';
import { SoundToggle } from '../../../../../lib/scenario-themes/SoundToggle';
import { Typewriter } from '../../../../../lib/scenario-themes/Typewriter';
import { useComposedExperience } from './useComposedExperience';

interface Props {
  data: ComposedExperienceSlideData;
  theme: Theme;
  isEditing?: boolean;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}

// Risograph palette (mockup-faithful spot inks, matching RisographPerspectiveLenses).
const INKS = ['#ff48a0', '#2453ff', '#00a878', '#ff6a13']; // pink, blue, green, orange
const PINK = INKS[0];
const BLUE = INKS[1];
const GREEN = INKS[2];
const ORANGE = INKS[3];
const CREAM = '#f4efe2'; // paper
const INK = '#16130d'; // text
const DIM = 'rgba(22,19,13,0.62)';
const PAPER_SHADOW = 'rgba(22,19,13,.22)';

const STYLE_ID = 'risograph-composed-scoped-styles';

// A cue runner bound to the risograph engine (from useThemeWorldAudio.playWith).
type PlayWith = ReturnType<typeof useThemeWorldAudio>['playWith'];

function scopedCss(): string {
  return `
.rsocx { position: relative; --reg: 2px; }
.rsocx * { box-sizing: border-box; }

.rsocx .rso-card {
  background: ${CREAM}; border: 3px solid ${INK}; box-shadow: 8px 9px 0 ${PAPER_SHADOW};
  position: relative; padding: 24px 26px;
}
.rsocx .rso-kicker { font-family: "Anton", "Archivo Black", sans-serif; font-size: 0.78rem; letter-spacing: 0.24em; text-transform: uppercase; color: ${BLUE}; margin-bottom: 10px; }
.rsocx .rso-prompt { font-family: inherit; font-size: 1.06rem; font-style: italic; color: ${INK}; margin: 0 0 8px; line-height: 1.4; border-left: 4px solid ${INK}; padding-left: 14px; }
.rsocx .rso-body { color: ${INK}; font-size: 1rem; line-height: 1.6; margin: 0; }
.rsocx .rso-quote { border-left: 4px solid ${ORANGE}; padding: 6px 0 6px 16px; color: ${INK}; font-style: italic; }

.rsocx textarea.rso-input {
  width: 100%; font-family: inherit; font-size: 1rem; color: ${INK}; line-height: 1.55;
  background: rgba(255,255,255,0.55); border: 2.5px solid ${INK}; border-radius: 0; padding: 12px 14px; resize: vertical;
  transition: box-shadow .2s;
}
.rsocx textarea.rso-input:focus { outline: none; box-shadow: 3px 3px 0 ${BLUE}; }

/* readings — the earlier value overprints the revised one, misregistered */
.rsocx .rso-scale-val { position: relative; font-family: "Anton", "Archivo Black", sans-serif; font-size: 3rem; line-height: 1; text-align: center; color: ${PINK}; }
.rsocx .rso-read-prior { position: absolute; left: 50%; top: 0; transform: translate(calc(-50% + var(--reg)), var(--reg)); color: ${BLUE}; mix-blend-mode: multiply; opacity: .85; pointer-events: none; }

.rsocx input[type="range"].rso-range { -webkit-appearance: none; appearance: none; width: 100%; height: 10px; border-radius: 0; outline: none; cursor: grab; border: 2.5px solid ${INK}; background: rgba(255,255,255,0.5); }
.rsocx input[type="range"].rso-range:disabled { cursor: default; }
.rsocx input[type="range"].rso-range::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 24px; height: 24px; border-radius: 0; background: ${PINK}; border: 2.5px solid ${INK}; box-shadow: 3px 3px 0 ${PAPER_SHADOW}; }
.rsocx input[type="range"].rso-range::-moz-range-thumb { width: 24px; height: 24px; border-radius: 0; border: 2.5px solid ${INK}; background: ${PINK}; box-shadow: 3px 3px 0 ${PAPER_SHADOW}; }

/* the change-of-mind OVERPRINT DIFF (re-rate standout) */
.rsocx .rso-track { position: relative; height: 52px; margin: 18px 0 6px; }
.rsocx .rso-rail { position: absolute; top: 50%; left: 0; right: 0; height: 10px; transform: translateY(-50%); border: 2.5px solid ${INK}; background: rgba(255,255,255,0.5); }
.rsocx .rso-range.rso-overlay { position: absolute; inset: 0; height: 100%; background: transparent; border: none; z-index: 4; }
.rsocx .rso-prior { position: absolute; top: 50%; width: 16px; height: 16px; transform: translate(-50%,-50%) rotate(45deg); border: 2.5px solid ${BLUE}; background: ${CREAM}; z-index: 3; }
.rsocx .rso-diff {
  position: absolute; top: 50%; height: 12px; transform: translateY(-50%); z-index: 2;
  background: repeating-linear-gradient(90deg, ${PINK} 0 8px, ${BLUE} 8px 16px);
  mix-blend-mode: multiply; border-top: 2.5px solid ${INK}; border-bottom: 2.5px solid ${INK};
  transition: left .5s cubic-bezier(0.2,0.9,0.3,1.2), width .5s cubic-bezier(0.2,0.9,0.3,1.2);
}
.rsocx .rso-stamp {
  position: absolute; top: -6px; transform: translate(-50%, 0) rotate(-9deg) scale(0.6); opacity: 0;
  font-family: "Anton", sans-serif; text-transform: uppercase; letter-spacing: 1px; font-size: 0.86rem; white-space: nowrap;
  color: ${ORANGE}; border: 3px solid ${ORANGE}; background: ${CREAM}; padding: 4px 9px 2px; mix-blend-mode: multiply; z-index: 5;
  transition: transform .3s cubic-bezier(.2,.9,.3,1.6), opacity .2s ease;
}
.rsocx .rso-stamp.stamped { transform: translate(-50%, 0) rotate(-9deg) scale(1); opacity: 1; }
.rsocx .rso-delta { font-family: "Anton", sans-serif; text-transform: uppercase; font-size: 0.78rem; letter-spacing: 0.06em; color: ${ORANGE}; margin-top: 12px; }
.rsocx .rso-ends { display: flex; justify-content: space-between; font-size: 0.74rem; color: ${DIM}; margin-top: 4px; text-transform: uppercase; letter-spacing: 1px; }

.rsocx .rso-btn {
  margin-top: 14px; font-family: "Anton", sans-serif; text-transform: uppercase; font-size: 0.94rem; letter-spacing: 0.06em; color: ${CREAM}; cursor: pointer;
  padding: 12px 22px 10px; border-radius: 0; border: 3px solid ${INK}; background: ${BLUE};
  box-shadow: 4px 5px 0 ${INK}; transition: transform .08s ease, box-shadow .08s ease, opacity .2s;
}
.rsocx .rso-btn:hover:not(:disabled) { transform: translate(-1px,-1px); box-shadow: 6px 7px 0 ${INK}; }
.rsocx .rso-btn:active:not(:disabled) { transform: translate(2px,3px); box-shadow: 1px 1px 0 ${INK}; }
.rsocx .rso-btn:disabled { opacity: 0.45; cursor: not-allowed; }

/* options */
.rsocx .rso-opt {
  width: 100%; text-align: left; color: ${INK}; background: rgba(255,255,255,0.5); border: 2.5px solid ${INK};
  border-radius: 0; padding: 12px 14px; font: inherit; cursor: pointer; box-shadow: 3px 3px 0 ${PAPER_SHADOW}; transition: transform .1s ease, box-shadow .1s ease, background .2s;
}
.rsocx .rso-opt:hover:not(:disabled) { transform: translate(-1px,-1px); box-shadow: 5px 5px 0 ${PAPER_SHADOW}; }
.rsocx .rso-opt.rso-on { background: ${GREEN}; color: ${CREAM}; box-shadow: 3px 3px 0 ${INK}; }
.rsocx .rso-opt:disabled { cursor: default; }

/* reveal — a print roller wipes ink across as the ref commits */
.rsocx .rso-sealed { border: 2.5px dashed ${INK}; border-radius: 0; padding: 20px; text-align: center; color: ${DIM}; font-family: "Anton", sans-serif; font-size: 0.82rem; letter-spacing: 0.16em; text-transform: uppercase; background: rgba(255,255,255,0.35); }
.rsocx .rso-reveal { overflow: hidden; }
.rsocx .rso-reveal::before { content: ""; position: absolute; inset: 0; border-radius: inherit; background: linear-gradient(90deg, ${PINK}, ${ORANGE}); mix-blend-mode: multiply; transform-origin: left center; animation: rsoRoller .7s cubic-bezier(0.2,0.9,0.3,1.1) forwards; pointer-events: none; }
@keyframes rsoRoller { 0% { transform: scaleX(1); } 100% { transform: scaleX(0); } }

/* ending — a filed stamp before the payoff */
.rsocx .rso-filed-wrap { text-align: center; margin-bottom: 16px; }
.rsocx .rso-filed { display: inline-block; font-family: "Anton", sans-serif; text-transform: uppercase; letter-spacing: 2px; font-size: 1.4rem; color: ${PINK}; border: 4px solid ${PINK}; border-radius: 8px; padding: 8px 16px 5px; transform: rotate(-9deg); mix-blend-mode: multiply; }

@media (prefers-reduced-motion: reduce) {
  .rsocx .rso-diff { transition: none !important; }
  .rsocx .rso-stamp { transition-duration: .001s !important; }
  .rsocx .rso-reveal::before { animation: none !important; opacity: 0; }
}
`;
}

function useScopedStyles(): void {
  useEffect(() => {
    let tag = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!tag) {
      tag = document.createElement('style');
      tag.id = STYLE_ID;
      tag.dataset.refcount = '0';
      tag.textContent = scopedCss();
      document.head.appendChild(tag);
    }
    tag.dataset.refcount = String(Number(tag.dataset.refcount || '0') + 1);
    return () => {
      const el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
      if (!el) return;
      const next = Number(el.dataset.refcount || '1') - 1;
      if (next <= 0) el.remove();
      else el.dataset.refcount = String(next);
    };
  }, []);
}

// ─────────────────────────────────────────────────────────────────────────────

export function ComposedExperienceRisograph({
  data,
  theme,
  isEditing,
  onActivityResponse,
  onComplete,
  savedResponse,
}: Props) {
  useScopedStyles();

  const { blocks, answers, setAnswer, complete, values, rollup } = useComposedExperience({
    data,
    isEditing,
    onActivityResponse,
    onComplete,
    savedResponse,
  });

  const { enabled: audioOn, toggle: toggleAudio, playWith } = useThemeWorldAudio('risograph');

  return (
    <div
      className="rsocx h-full w-full overflow-y-auto"
      style={{
        background: `radial-gradient(900px 600px at 12% -5%, rgba(255,72,160,0.06), transparent 60%), radial-gradient(800px 600px at 100% 15%, rgba(36,83,255,0.06), transparent 55%), ${CREAM}`,
        color: INK,
        fontFamily: theme.fonts.body,
      }}
    >
      <ThemeWorldAtmosphere worldId="risograph" primary={INKS[1]} accent={INKS[0]} background={CREAM} style={{ zIndex: 0 }} />
      <SoundToggle enabled={audioOn} onToggle={toggleAudio} theme={theme} />

      <div className="relative" style={{ zIndex: 10 }}>
        <div className="mx-auto" style={{ maxWidth: 720, padding: '56px 24px 110px' }}>
          <header style={{ marginBottom: 30 }}>
            <div style={{ fontFamily: '"Anton", sans-serif', fontSize: 12, letterSpacing: '0.28em', color: BLUE, textTransform: 'uppercase', marginBottom: 14 }}>
              ◼ Press once · decide
            </div>
            <h1 style={{ margin: 0 }}>
              <Typewriter
                text={data.title}
                speed={30}
                caretColor={PINK}
                style={{
                  fontFamily: '"Anton", "Archivo Black", sans-serif',
                  fontWeight: 400,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontSize: 'clamp(1.9rem, 5vw, 3rem)',
                  lineHeight: 1.0,
                  color: INK,
                  textShadow: `2px 2px 0 rgba(255,72,160,0.55)`,
                  display: 'inline-block',
                }}
              />
            </h1>
            {data.subtitle && <p style={{ color: DIM, fontSize: 15, fontStyle: 'italic', margin: '12px 0 0' }}>{data.subtitle}</p>}
          </header>

          <div className="space-y-5">
            {blocks.map((block) => (
              <RsoBlock
                key={block.id}
                block={block}
                answers={answers}
                answer={answers[block.id]}
                onAnswer={(a) => setAnswer(block.id, a)}
                playWith={playWith}
              />
            ))}
          </div>

          {complete && data.conclusion && (
            <div className="mt-10">
              <div className="rso-filed-wrap">
                <span className="rso-filed">Case Filed</span>
              </div>
              <ExperienceClose
                conclusion={data.conclusion}
                theme={theme}
                values={values}
                outcome={data.scoring.mode === 'none' ? undefined : String(Math.round(rollup * 100))}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

interface RsoBlockProps {
  block: CompositionBlock;
  answers: Answers;
  answer: BlockAnswer | undefined;
  onAnswer: (a: BlockAnswer) => void;
  playWith: PlayWith;
}

function RsoBlock({ block, answers, answer, onAnswer, playWith }: RsoBlockProps) {
  switch (block.type) {
    case 'stimulus':
      return <RsoStimulus block={block} />;
    case 'rate-slider':
      return <RsoRateSlider block={block} answers={answers} answer={answer} onAnswer={onAnswer} playWith={playWith} />;
    case 'confidence':
      return <RsoRateSlider block={block as unknown as RateSliderBlock} answers={answers} answer={answer} onAnswer={onAnswer} playWith={playWith} confidence />;
    case 'reveal':
      return <RsoReveal block={block} answers={answers} playWith={playWith} />;
    case 'commit-box':
      return <RsoCommitBox block={block} answer={answer} onAnswer={onAnswer} playWith={playWith} />;
    case 'choice':
      return <RsoChoice block={block} answer={answer} onAnswer={onAnswer} playWith={playWith} />;
    case 'select-evidence':
      return <RsoSelectEvidence block={block} answer={answer} onAnswer={onAnswer} playWith={playWith} />;
    case 'sort-zones':
      return <RsoSortZones block={block} answer={answer} onAnswer={onAnswer} playWith={playWith} />;
    case 'flag-in-text':
      return <RsoFlagInText block={block} answer={answer} onAnswer={onAnswer} playWith={playWith} />;
    case 'perspective-lens':
      return <RsoPerspective block={block} answer={answer} onAnswer={onAnswer} playWith={playWith} />;
    default:
      return null;
  }
}

function Card({ children }: { children: React.ReactNode }) {
  return <div className="rso-card">{children}</div>;
}

// ── stimulus ──────────────────────────────────────────────────────────────────
function RsoStimulus({ block }: { block: StimulusBlock }) {
  return (
    <Card>
      {block.content.heading && <div className="rso-kicker">{block.content.heading}</div>}
      <p className="rso-body">{block.content.body}</p>
    </Card>
  );
}

// ── rate-slider (+ confidence) — the change-of-mind OVERPRINT DIFF standout ─────
function RsoRateSlider({
  block,
  answers,
  answer,
  onAnswer,
  playWith,
  confidence,
}: {
  block: RateSliderBlock;
  answers: Answers;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  playWith: PlayWith;
  confidence?: boolean;
}) {
  const { min, max, step } = block.structure;
  const committed = (answer as { value: number } | undefined)?.value;
  const [pos, setPos] = useState<number>(committed ?? Math.round((min + max) / 2));
  const locked = committed !== undefined;
  const value = locked ? committed : pos;
  const prior = block.ref ? (answers[block.ref] as { value?: number } | undefined)?.value : undefined;
  const pct = (v: number) => ((v - min) / (max - min)) * 100;
  const delta = prior !== undefined ? value - prior : undefined;

  return (
    <Card>
      <div className="rso-kicker">{confidence ? 'Confidence' : prior !== undefined ? 'Second press' : 'First press'}</div>
      <p className="rso-prompt">{block.content.prompt}</p>
      {/* Two-ink misregistered print of the CURRENT reading (same digit offset
          in a second ink) — the riso look, still legible. The prior reading is
          shown by the track diamond + the MOVED delta, not stacked on the number. */}
      <div className="rso-scale-val">
        <span className="rso-read-prior" aria-hidden>{value}</span>
        <span>{value}</span>
      </div>
      <div className="rso-track">
        <div className="rso-rail" />
        {prior !== undefined && (
          <span
            className="rso-diff"
            style={{ left: `${Math.min(pct(prior), pct(value))}%`, width: `${Math.abs(pct(value) - pct(prior))}%` }}
            aria-hidden
          />
        )}
        {prior !== undefined && <span className="rso-prior" style={{ left: `${pct(prior)}%` }} aria-hidden />}
        {prior !== undefined && delta !== undefined && (
          <span
            className={`rso-stamp${locked ? ' stamped' : ''}`}
            style={{ left: `${(pct(prior) + pct(value)) / 2}%` }}
            aria-hidden
          >
            {delta === 0 ? 'HELD' : `MOVED ${delta > 0 ? '+' : '−'}${Math.abs(delta)}`}
          </span>
        )}
        <input
          type="range"
          className="rso-range rso-overlay"
          min={min}
          max={max}
          step={step ?? 1}
          value={value}
          disabled={locked}
          onChange={(e) => {
            const v = Number(e.target.value);
            setPos(v);
            playWith((eng) => playRisoRoller(eng));
          }}
        />
      </div>
      <div className="rso-ends">
        <span>{block.content.minLabel ?? min}</span>
        <span>{block.content.maxLabel ?? max}</span>
      </div>
      {prior !== undefined && delta !== undefined && (
        <div className="rso-delta">
          {delta === 0 ? 'You held the line' : `You moved ${delta > 0 ? '+' : '−'}${Math.abs(delta)} — from ${prior} to ${value}`}
        </div>
      )}
      {!locked && (
        <button
          className="rso-btn"
          onClick={() => {
            playWith((eng) => playRisoStamp(eng, 1, 0));
            onAnswer({ value: pos });
          }}
        >
          {prior !== undefined ? 'Stamp your revision' : 'Press your reading'}
        </button>
      )}
    </Card>
  );
}

// ── reveal — a print roller wipes ink across once its ref commits ──────────────
function RsoReveal({ block, answers, playWith }: { block: RevealBlock; answers: Answers; playWith: PlayWith }) {
  const unlocked = answers[block.ref] !== undefined;
  useEffect(() => {
    if (unlocked) playWith((eng) => playRisoRoller(eng));
  }, [unlocked, playWith]);
  if (!unlocked) {
    return <div className="rso-sealed">◼ Stamp your reading to run the next pass ◼</div>;
  }
  return (
    <div className="rso-card rso-reveal">
      <div className="rso-kicker">{block.content.heading || 'The next pass'}</div>
      <p className="rso-body">{block.content.body}</p>
    </div>
  );
}

// ── commit-box — held, then set into a filed clipping ─────────────────────────
function RsoCommitBox({
  block,
  answer,
  onAnswer,
  playWith,
}: {
  block: CommitBoxBlock;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  playWith: PlayWith;
}) {
  const committed = (answer as { text: string } | undefined)?.text;
  const [text, setText] = useState(committed ?? '');
  const locked = committed !== undefined;
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  const ok = words >= (block.structure.minWords ?? 1);
  return (
    <Card>
      <div className="rso-kicker">What you'll file</div>
      <p className="rso-prompt">{block.content.prompt}</p>
      {locked ? (
        <div className="rso-quote">{committed}</div>
      ) : (
        <>
          <textarea className="rso-input" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder={block.content.placeholder} />
          <button
            className="rso-btn"
            disabled={!ok}
            onClick={() => {
              playWith((eng) => playRisoStamp(eng, 1, 0));
              onAnswer({ text: text.trim() });
            }}
          >
            File this
          </button>
        </>
      )}
    </Card>
  );
}

// ── choice ────────────────────────────────────────────────────────────────────
function RsoChoice({
  block,
  answer,
  onAnswer,
  playWith,
}: {
  block: ChoiceBlock;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  playWith: PlayWith;
}) {
  const chosen = (answer as { optionId: string } | undefined)?.optionId;
  const chosenOpt = block.content.options.find((o) => o.id === chosen);
  return (
    <Card>
      <p className="rso-prompt">{block.content.prompt}</p>
      <div className="space-y-2">
        {block.content.options.map((opt) => (
          <button
            key={opt.id}
            disabled={!!chosen}
            onClick={() => {
              playWith((eng) => playRisoStamp(eng, 1, 0));
              onAnswer({ optionId: opt.id });
            }}
            className={`rso-opt${opt.id === chosen ? ' rso-on' : ''}`}
            style={{ opacity: chosen && opt.id !== chosen ? 0.5 : 1 }}
          >
            {opt.text}
          </button>
        ))}
      </div>
      {chosenOpt?.feedback && <p style={{ marginTop: 12, fontSize: '0.9rem', color: DIM, fontStyle: 'italic' }}>{chosenOpt.feedback}</p>}
    </Card>
  );
}

// ── select-evidence — stamped selections ──────────────────────────────────────
function RsoSelectEvidence({
  block,
  answer,
  onAnswer,
  playWith,
}: {
  block: SelectEvidenceBlock;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  playWith: PlayWith;
}) {
  const committed = (answer as { selected: string[] } | undefined)?.selected;
  const [selected, setSelected] = useState<string[]>(committed ?? []);
  const locked = committed !== undefined;
  const max = block.structure.maxSelections;
  const atMax = max !== undefined && selected.length >= max;
  const toggle = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : atMax ? s : [...s, id]));
  return (
    <Card>
      {block.content.claim && <div className="rso-quote" style={{ marginBottom: 12 }}>{block.content.claim}</div>}
      <p className="rso-prompt">{block.content.prompt}</p>
      <div className="space-y-2">
        {block.content.items.map((it) => (
          <button
            key={it.id}
            disabled={locked || (!selected.includes(it.id) && atMax)}
            onClick={() => {
              playWith((eng) => playRisoStamp(eng, 1, 0));
              toggle(it.id);
            }}
            className={`rso-opt${selected.includes(it.id) ? ' rso-on' : ''}`}
          >
            {it.text}
          </button>
        ))}
      </div>
      {max !== undefined && !locked && <p style={{ marginTop: 8, fontSize: '0.72rem', color: DIM }}>{selected.length}/{max} chosen</p>}
      {!locked && (
        <button className="rso-btn" disabled={selected.length === 0} onClick={() => onAnswer({ selected })}>
          File these
        </button>
      )}
    </Card>
  );
}

// ── sort-zones ────────────────────────────────────────────────────────────────
function RsoSortZones({
  block,
  answer,
  onAnswer,
  playWith,
}: {
  block: SortZonesBlock;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  playWith: PlayWith;
}) {
  const committed = (answer as { placement: Record<string, string> } | undefined)?.placement;
  const [placement, setPlacement] = useState<Record<string, string>>(committed ?? {});
  const locked = committed !== undefined;
  const allPlaced = block.content.items.every((it) => placement[it.id]);
  return (
    <Card>
      <p className="rso-prompt">{block.content.prompt}</p>
      <div className="space-y-3">
        {block.content.items.map((it) => (
          <div key={it.id}>
            <p style={{ fontSize: '0.92rem', color: INK, margin: '0 0 6px' }}>{it.text}</p>
            <div className="flex flex-wrap gap-2">
              {block.content.zones.map((z) => (
                <button
                  key={z.id}
                  disabled={locked}
                  onClick={() => {
                    playWith((eng) => playRisoStamp(eng, 1, 0));
                    setPlacement((p) => ({ ...p, [it.id]: z.id }));
                  }}
                  className={`rso-opt${placement[it.id] === z.id ? ' rso-on' : ''}`}
                  style={{ width: 'auto', padding: '6px 14px', fontSize: '0.82rem' }}
                >
                  {z.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      {!locked && (
        <button className="rso-btn" disabled={!allPlaced} onClick={() => onAnswer({ placement })}>
          File the sort
        </button>
      )}
    </Card>
  );
}

// ── flag-in-text ──────────────────────────────────────────────────────────────
function RsoFlagInText({
  block,
  answer,
  onAnswer,
  playWith,
}: {
  block: FlagInTextBlock;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  playWith: PlayWith;
}) {
  const committed = (answer as { flagged: string[] } | undefined)?.flagged;
  const [flagged, setFlagged] = useState<string[]>(committed ?? []);
  const locked = committed !== undefined;
  const toggle = (id: string) => setFlagged((f) => (f.includes(id) ? f.filter((x) => x !== id) : [...f, id]));
  return (
    <Card>
      <p className="rso-prompt">{block.content.prompt}</p>
      <p className="rso-body" style={{ lineHeight: 1.9 }}>
        {block.content.segments.map((seg) => {
          const on = flagged.includes(seg.id);
          const wasRight = locked && seg.flagged === true;
          const wasWrong = locked && on && seg.flagged !== true;
          return (
            <span
              key={seg.id}
              onClick={() => {
                if (locked) return;
                playWith((eng) => playRisoStamp(eng, 1, 0));
                toggle(seg.id);
              }}
              style={{
                cursor: locked ? 'default' : 'pointer',
                background: on ? (wasWrong ? 'rgba(255,72,160,0.22)' : 'rgba(0,168,120,0.20)') : 'transparent',
                borderBottom: locked
                  ? `2.5px solid ${wasRight ? GREEN : wasWrong ? PINK : 'transparent'}`
                  : on
                    ? `2.5px solid ${GREEN}`
                    : '2.5px solid transparent',
                borderRadius: 0,
                padding: '0 2px',
              }}
            >
              {seg.text}{' '}
            </span>
          );
        })}
      </p>
      {!locked && (
        <button className="rso-btn" disabled={flagged.length === 0} onClick={() => onAnswer({ flagged })}>
          Mark {flagged.length} span{flagged.length === 1 ? '' : 's'}
        </button>
      )}
    </Card>
  );
}

// ── perspective-lens ──────────────────────────────────────────────────────────
function RsoPerspective({
  block,
  answer,
  onAnswer,
  playWith,
}: {
  block: PerspectiveLensBlock;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  playWith: PlayWith;
}) {
  const committed = (answer as { takes: Record<string, string> } | undefined)?.takes;
  const [takes, setTakes] = useState<Record<string, string>>(committed ?? {});
  const locked = committed !== undefined;
  const allFilled = block.content.lenses.every((l) => (takes[l.id] ?? '').trim().length > 0);
  return (
    <Card>
      <p className="rso-prompt">{block.content.prompt}</p>
      <div className="space-y-3">
        {block.content.lenses.map((lens, i) => (
          <div key={lens.id}>
            <p style={{ fontFamily: '"Anton", sans-serif', fontSize: '0.82rem', textTransform: 'uppercase', color: INKS[i % INKS.length], letterSpacing: '0.08em', margin: '0 0 3px' }}>{lens.label}</p>
            {lens.hint && <p style={{ fontSize: '0.78rem', color: DIM, margin: '0 0 4px' }}>{lens.hint}</p>}
            {locked ? (
              <div className="rso-quote">{takes[lens.id]}</div>
            ) : (
              <textarea
                className="rso-input"
                rows={2}
                value={takes[lens.id] ?? ''}
                onChange={(e) => setTakes((tk) => ({ ...tk, [lens.id]: e.target.value }))}
              />
            )}
          </div>
        ))}
      </div>
      {!locked && (
        <button
          className="rso-btn"
          disabled={!allFilled}
          onClick={() => {
            playWith((eng) => playRisoStamp(eng, 1, 0));
            onAnswer({ takes });
          }}
        >
          File these
        </button>
      )}
    </Card>
  );
}
